/**
 * Lorem Ipsum Generator Service
 * Implements business logic for generating Lorem Ipsum placeholder text
 */

import { EncodingOperation, EncoderToolType } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';

export type LoremType = 'paragraphs' | 'sentences' | 'words';

const LOREM_WORDS = [
  'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
  'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
  'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
  'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
  'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
  'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
  'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
  'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum'
];

export class LoremIpsumService {
  /**
   * Generate Lorem Ipsum text
   */
  static async generate(
    quantity: number,
    type: LoremType = 'paragraphs',
    startWithLorem: boolean = true
  ): Promise<EncodingOperation> {
    try {
      // Validate quantity
      if (quantity < 1 || quantity > 100) {
        throw new ValidationError(
          'Quantity must be between 1 and 100',
          EncoderToolType.LOREM_IPSUM,
          'Enter a value between 1 and 100'
        );
      }

      // Generate Lorem Ipsum text
      let text: string;
      switch (type) {
        case 'words':
          text = this.generateWords(quantity, startWithLorem);
          break;
        case 'sentences':
          text = this.generateSentences(quantity, startWithLorem);
          break;
        case 'paragraphs':
        default:
          text = this.generateParagraphs(quantity, startWithLorem);
      }

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.LOREM_IPSUM,
        operationType: 'encode',
        inputData: `${quantity} ${type}`,
        outputData: text,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          type,
          quantity,
          startWithLorem,
          length: text.length,
          wordCount: text.split(/\s+/).length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to generate Lorem Ipsum',
        EncoderToolType.LOREM_IPSUM,
        'Please check your settings and try again'
      );
    }
  }

  /**
   * Generate words
   */
  private static generateWords(count: number, startWithLorem: boolean): string {
    const words: string[] = [];

    if (startWithLorem && count >= 2) {
      words.push('Lorem', 'ipsum');
      count -= 2;
    }

    for (let i = 0; i < count; i++) {
      words.push(LOREM_WORDS[Math.floor(Math.random() * LOREM_WORDS.length)]);
    }

    return words.join(' ');
  }

  /**
   * Generate sentences
   */
  private static generateSentences(count: number, startWithLorem: boolean): string {
    const sentences: string[] = [];

    for (let i = 0; i < count; i++) {
      const wordCount = 8 + Math.floor(Math.random() * 8); // 8-15 words per sentence
      const useLoremStart = startWithLorem && i === 0;
      const words = this.generateWords(wordCount, useLoremStart).split(' ');
      words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
      sentences.push(words.join(' ') + '.');
    }

    return sentences.join(' ');
  }

  /**
   * Generate paragraphs
   */
  private static generateParagraphs(count: number, startWithLorem: boolean): string {
    const paragraphs: string[] = [];

    for (let i = 0; i < count; i++) {
      const sentenceCount = 4 + Math.floor(Math.random() * 4); // 4-7 sentences per paragraph
      const useLoremStart = startWithLorem && i === 0;
      paragraphs.push(this.generateSentences(sentenceCount, useLoremStart));
    }

    return paragraphs.join('\n\n');
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `lorem-ipsum-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
